import { useCallback } from 'react';
import { Grid, Paper } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import Route from 'routes/Routes';
import { trackEvent, GACommonEventAction, GAEventCategory } from 'services/googleAnalytics';
import { useSelectedLinkContext } from 'contexts/SelectedLinkContext';
import { navigateToSelectedChannelRoute } from 'utils/history';
import { makeStyles } from '@mui/styles';
import { COLORS } from 'theme/MainTheme';
import { useNoWebUserLinkSnackbar } from 'hooks';
import {type CustomSvgIcon} from 'components/icons';

const useStyles = makeStyles(() => ({
  container: {
    borderRadius: 12,
    cursor: 'pointer',
    padding: 25,

    '&:hover': {
      backgroundColor: COLORS.LIGHT_BLUE,
    },
  },
  icon: {
    width: 60,
    height: 60,
  },
  iconContainer: {
    textAlign: 'right',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  description: {
    fontSize: 16,
    fontWeight: 300,
    height: 50,
    paddingTop: 10,
  },
}), { name: 'AdminCategory' });

export type AdminCategoryProps = {
  descriptionMessageId: string;
  icon: CustomSvgIcon;
  route: Route;
  titleMessageId: string;
};

const AdminCategory = ({
  descriptionMessageId,
  icon: Icon,
  route,
  titleMessageId,
}: AdminCategoryProps): JSX.Element => {
  const classes = useStyles();

  const { selectedLink } = useSelectedLinkContext();
  const showNoWebUserLinkSnackbar = useNoWebUserLinkSnackbar(selectedLink);
  const intl = useIntl();

  const handleClick = useCallback(() => {
    if (!selectedLink) {
      showNoWebUserLinkSnackbar();
    } else {
      trackEvent(
        GAEventCategory.CATEGORY,
        GACommonEventAction.CLICK,
        intl.formatMessage({ id: titleMessageId }),
      );

      navigateToSelectedChannelRoute(selectedLink, route);
    }
  }, [selectedLink, showNoWebUserLinkSnackbar, intl, titleMessageId, route]);

  return (
    <Grid item md={3} xs={12}>
      <Paper
        className={classes.container}
        data-testid="category-paper"
        onClick={handleClick}
      >
        <Grid container>
          <Grid className={classes.title} item xs={8}>
            <FormattedMessage id={titleMessageId} />
          </Grid>

          <Grid className={classes.iconContainer} item xs={4}>
            <Icon className={classes.icon} />
          </Grid>

          <Grid className={classes.description} item xs={12}>
            <FormattedMessage id={descriptionMessageId} />
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default AdminCategory;
